/**
 * Vehicle Set Picker — Variant Selection Override
 *
 * Replaces Shopify's default variant_picker JS behavior on the product.optimized template.
 * Architected as an OVERRIDE, not a fork — when Motion theme updates, this can be re-applied.
 *
 * Behavior:
 *   - Wires up <input type="radio" name="id"> elements rendered by snippets/vehicle-set-picker.liquid
 *   - On selection change:
 *       * Updates [data-product-price] display with formatted price
 *       * Updates Buy Button text + disabled state (Sold Out vs Add to Cart)
 *       * Dispatches a 'variant:change' CustomEvent so other blocks (FBT, inventory)
 *         can listen and update
 *   - Auto-selects first available variant on load
 *
 * Required DOM:
 *   [data-vehicle-picker]                  Container fieldset
 *   [data-vehicle-picker-data]             <script type="application/json"> with variants[]
 *   [data-variant-input]                   Each radio input
 *   [data-product-price]                   Price display target (in product form)
 *   [data-add-to-cart]                     Buy button
 *
 * Notes:
 *   - Listens for 'change' events (not 'click') so keyboard nav (Tab + Space) works.
 *   - Multiple instances on one page are supported via per-container scoping.
 */

(function () {
  'use strict';

  function initPicker(container) {
    const dataEl = container.querySelector('[data-vehicle-picker-data]');
    if (!dataEl) {
      console.warn('[vehicle-set-picker] No data element found in', container);
      return;
    }

    let payload;
    try {
      payload = JSON.parse(dataEl.textContent);
    } catch (err) {
      console.error('[vehicle-set-picker] Failed to parse variant data', err);
      return;
    }

    const variants = payload.variants || [];
    if (!variants.length) return;

    // Find the product form this picker belongs to.
    const form = container.closest('form[action*="/cart/add"]') || document;

    // Scope these selectors to the section, not just the form, so the
    // stock pill (which lives in the price row above the form) is found.
    const section = container.closest('[data-section-id]') || document;
    const priceEl = section.querySelector('[data-product-price]');
    const stockPillEl = section.querySelector('[data-stock-pill]');
    const ctaEl = form.querySelector('[data-add-to-cart]');
    const ctaTextEl = ctaEl ? (ctaEl.querySelector('[data-cta-text]') || ctaEl) : null;

    function applyVariant(variantId) {
      const v = variants.find((x) => x.id === Number(variantId));
      if (!v) return;

      // Price
      if (priceEl) {
        priceEl.innerHTML = v.price_formatted;
      }

      // Stock pill
      if (stockPillEl) {
        if (v.available) {
          stockPillEl.textContent = 'In Stock';
          stockPillEl.classList.remove('is-soldout');
        } else {
          stockPillEl.textContent = 'Sold Out';
          stockPillEl.classList.add('is-soldout');
        }
      }

      // CTA
      if (ctaEl && ctaTextEl) {
        if (v.available) {
          ctaEl.removeAttribute('disabled');
          ctaTextEl.textContent = 'Add to Cart';
          ctaEl.classList.remove('is-soldout');
        } else {
          ctaEl.setAttribute('disabled', 'disabled');
          ctaTextEl.textContent = 'Sold Out';
          ctaEl.classList.add('is-soldout');
        }
      }

      // Broadcast for other blocks (FBT, inventory status, etc.) to react.
      container.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: v, productId: container.dataset.productId },
        })
      );
    }

    // Wire up radios
    const radios = container.querySelectorAll('[data-variant-input]');
    radios.forEach((radio) => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          applyVariant(e.target.value);
        }
      });
    });

    // Initial state — find the currently-checked radio (Liquid pre-selected first available)
    const checked = container.querySelector('[data-variant-input]:checked');
    if (checked) {
      applyVariant(checked.value);
    }
  }

  function init() {
    document.querySelectorAll('[data-vehicle-picker]').forEach(initPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section reload (theme customizer)
  document.addEventListener('shopify:section:load', init);
})();
